import React, { useEffect, useState, useRef } from "react";
import { Modal } from "react-bootstrap";

export default function ZplLabelPrintButton({
  columnLabels,
  labelData,
  rowsData,
}) {
  /*   All States
   ********************************************* */
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [printingFirstLabel, setPrintingFirstLabel] = useState(false);

  const iframeRef = useRef(null);

  /*   All Functions
   ********************************************* */
  const getColumnValueForRow = (column, rowIndex) => {
    if (!column || !labelData) return null;
    let colIndex = columnLabels?.findIndex((col) => col === column);
    // Get value from the specified row
    let rowValue = labelData?.[rowIndex]?.[colIndex];
    return rowValue?.value;
  };

  // Update the getMultipleColumnValueForRow function:
  const getMultipleColumnValueForRow = (hide, columns, rowIndex) => {
    if (!columns || !labelData) return null;

    let value = "";
    columns?.forEach((item) => {
      // Handle both new object format and old string format
      const columnName = typeof item === "string" ? item : item.name;
      const columnIndex =
        typeof item === "string"
          ? columnLabels.findIndex((col) => col === item)
          : item.index;

      // Get value using index when available
      let columnValue;
      if (
        columnIndex !== undefined &&
        columnIndex >= 0 &&
        labelData[rowIndex]
      ) {
        columnValue = labelData[rowIndex][columnIndex]?.value;
      } else {
        // Fallback to column name search
        columnValue = getColumnValueForRow(columnName, rowIndex);
      }

      if (columnValue) {
        const prefix = hide ? "" : `${columnName} : `;
        value = value
          ? `${value}\u00A0\u00A0${prefix}${columnValue}`
          : `${prefix}${columnValue}`;
      }
    });

    return value;
  };

  const getSingleRowTypeValueForRow = (row, rowIndex) => {
    let value = "";

    if (row?.rowValues?.customHeader) {
      value = row.rowValues.customHeader;
    }

    const columnValue = getColumnValueForRow(row?.rowValues?.column, rowIndex);
    if (columnValue) {
      const prefix = row?.rowValues?.hideHeader
        ? ""
        : `${row?.rowValues?.column} : `;
      value = value
        ? `${value}\u00A0\u00A0${prefix}${columnValue}`
        : `${prefix}${columnValue}`;
    }

    return value;
  };

  const getMultipleRowTypeValueForRow = (row, rowIndex) => {
    let value = "";

    if (row?.rowValues?.customHeader) {
      value = row.rowValues.customHeader;
    }

    const multipleValues = getMultipleColumnValueForRow(
      row?.rowValues?.hideHeader,
      row?.rowValues?.columns,
      rowIndex
    );

    if (multipleValues) {
      value = value ? `${value}\u00A0\u00A0${multipleValues}` : multipleValues;
    }

    return value;
  };

  const getRowTextForDataRow = (rowData, dataRowIndex) => {
    if (!rowData) return "";

    if (rowData.rowType === "manual") {
      return rowData.rowValues?.customHeader || "";
    }

    if (rowData.rowType === "single") {
      return getSingleRowTypeValueForRow(rowData, dataRowIndex);
    }

    if (rowData.rowType === "multiple") {
      return getMultipleRowTypeValueForRow(rowData, dataRowIndex);
    }

    return "";
  };

  const generateZplCodeForRow = (dataRowIndex) => {
    // Get text for all rows from the array for this specific data row
    const rowTexts = rowsData.map(
      (row) => getRowTextForDataRow(row, dataRowIndex) || ""
    );

    // Label dimensions - 4"x2"
    const labelWidth = 812; // 4 inches at 203 DPI

    // Matching C# implementation but increasing font weight for first 3 rows
    const fontSizes = [
      { height: 45, width: 38 }, // Row 1 - slightly larger & bolder
      { height: 45, width: 38 }, // Row 2 - slightly larger & bolder
      { height: 45, width: 38 }, // Row 3 - slightly larger & bolder
      { height: 45, width: 38 }, // Row 4
      { height: 45, width: 38 }, // Row 5
      { height: 45, width: 38 }, // Row 6
      // { height: 38, width: 32 }, // Keeping old Size.
    ];

    // Define line counts for each row (last 3 rows can have 2 lines)
    const rowLines = [1, 1, 1, 2, 2, 2];

    // Define Y positions exactly as in the C# code
    const rowPositions = [20, 65, 110, 155, 240, 325];

    // Horizontal margin and text width
    const horizontalMargin = 30;
    const textWidth = 750; // Matching C# FB750 parameter

    // Generate ZPL code for each row
    let zplElements = "";
    for (let i = 0; i < 6; i++) {
      // Skip if we have fewer than 6 rows defined in rowsData
      if (i >= rowsData.length) continue;

      // Use regular ^A0 font command but increase dimensions for first 3 rows
      // We're not using B parameter as it causes rotation issues
      zplElements += `^FO${horizontalMargin},${rowPositions[i]}^A0,${fontSizes[i].height},${fontSizes[i].width}^FB${textWidth},${rowLines[i]},0,C^FD${rowTexts[i]}^FS\n      `;
    }

    return `
      ^XA
      ^PW812^LL406    ^FX Set label size to 4"x2" (203dpi: 812x406 dots)
      
      ${zplElements}
      ^XZ
    `;
  };

  const printFirstLabel = () => {
    if (!labelData || labelData.length === 0) return;

    setPrintingFirstLabel(true);

    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentWindow || iframe.contentDocument;
      if (doc.document) {
        doc.document.open();

        // Start HTML document
        doc.document.write(`
          <html>
            <body>
        `);

        // Generate and add ZPL code for only the first row
        const zplCode = generateZplCodeForRow(0);
        doc.document.write(`<pre>${zplCode}</pre>`);

        // Close HTML document
        doc.document.write(`
            </body>
          </html>
        `);

        doc.document.close();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Show confirmation dialog after printing first label
        setTimeout(() => {
          setShowConfirmDialog(true);
          setPrintingFirstLabel(false);
        }, 500);
      }
    }
  };

  const printRemainingLabels = () => {
    if (!labelData || labelData.length <= 1) return;

    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const doc = iframe.contentWindow || iframe.contentDocument;
      if (doc.document) {
        doc.document.open();

        // Start HTML document
        doc.document.write(`
          <html>
            <body>
        `);

        // Generate and add ZPL code for remaining rows (skip first row)
        for (let i = 1; i < labelData.length; i++) {
          const zplCode = generateZplCodeForRow(i);
          doc.document.write(`<pre>${zplCode}</pre>`);
        }

        // Close HTML document
        doc.document.write(`
            </body>
          </html>
        `);

        doc.document.close();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }
    }

    // Close the dialog
    setShowConfirmDialog(false);
  };

  const handleStartPrinting = () => {
    // Check if we have any data to print
    if (!labelData || labelData.length === 0) {
      alert("No label data to print.");
      return;
    }

    // If only one label, just print it directly
    if (labelData.length === 1) {
      printFirstLabel();
      return;
    }

    // For multiple labels, start the confirmation workflow
    printFirstLabel();
  };

  const cancelPrinting = () => {
    setShowConfirmDialog(false);
  };

  /*   All UseEffects
   ********************************************* */

  return (
    <>
      <button
        onClick={handleStartPrinting}
        className="btn btn-info btn-sm"
        disabled={!labelData || labelData.length === 0 || printingFirstLabel}
      >
        {printingFirstLabel ? "Printing..." : "Print ZPL"}
      </button>

      {/* Hidden iframe for printing */}
      <iframe
        ref={iframeRef}
        style={{ display: "none" }}
        title="ZPL Print Frame"
      ></iframe>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmDialog}
        onHide={cancelPrinting}
        backdrop="static"
        centered
      >
        <Modal.Header className="bg-info text-white">
          <Modal.Title>Confirm Printing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <i className="mdi mdi-printer-check mdi-48px text-info"></i>
          </div>
          <p className="mb-1">First label has been sent to printer.</p>
          <p className="mb-0">
            <strong>Is the printed label correct?</strong>
          </p>

          <div className="bg-light p-2 mt-3 rounded-2">
            <p className="mb-0 small">
              <i className="mdi mdi-information-outline me-1"></i>
              Clicking "Yes" will print the remaining{" "}
              {labelData ? labelData.length - 1 : 0} labels.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={cancelPrinting}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={printRemainingLabels}
            disabled={!labelData || labelData.length <= 1}
          >
            Yes, Print Remaining Labels
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
